import React, { useMemo, useState } from "react";
import InventoryService from "../../../services/InventoryService";

const buildFormData = (product, warehouse) => ({
  productId: product.id,
  warehouseId: warehouse.id,
  price: warehouse.price != null ? String(warehouse.price) : "",
  stock: warehouse.stock != null ? String(warehouse.stock) : "",
  discount_percent:
    warehouse.discount_percent != null ? String(warehouse.discount_percent) : "",
  min_order: warehouse.min_order != null ? String(warehouse.min_order) : "",
  max_order: warehouse.max_order != null ? String(warehouse.max_order) : "",
  order_multiple:
    warehouse.order_multiple != null ? String(warehouse.order_multiple) : "",
  is_active: warehouse.inventory_is_active ?? true,
});

const inputClassName =
  "w-16 md:w-20 rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 " +
  "focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-400 " +
  "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none";

const WarehouseCard = ({ product, warehouse }) => {
  const initialData = useMemo(
    () => buildFormData(product, warehouse),
    [product, warehouse]
  );

  const [formData, setFormData] = useState(initialData);
  const [savedData, setSavedData] = useState(initialData);
  const [isSaving, setIsSaving] = useState(false);

  const isDirty = JSON.stringify(formData) !== JSON.stringify(savedData);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!isDirty) return;

    setIsSaving(true);
    try {
      await InventoryService.saveProductInventory(formData);
      setSavedData(formData);
    } catch {
      alert("خطا در ذخیره سازی");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full max-w-full overflow-x-hidden rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex min-w-[140px] items-center gap-1">
          <span className="truncate font-medium text-gray-800" title={warehouse.title}>
            {warehouse.title}
          </span>
          {!warehouse.is_active && (
            <span className="whitespace-nowrap rounded-full bg-gray-200 px-1.5 py-0.5 text-[10px] text-gray-600">
              غیرفعال
            </span>
          )}
        </div>

        <div className="flex items-center gap-1">
          <label className="whitespace-nowrap text-xs font-medium text-gray-700">
            قیمت:
          </label>
          <input
            type="number"
            value={formData.price}
            onChange={(e) => handleChange("price", e.target.value)}
            placeholder="0"
            className={inputClassName}
          />
        </div>

        <div className="flex items-center gap-1">
          <label className="whitespace-nowrap text-xs font-medium text-gray-700">
            موجودی:
          </label>
          <input
            type="number"
            value={formData.stock}
            onChange={(e) => handleChange("stock", e.target.value)}
            placeholder="0"
            className={inputClassName}
          />
        </div>

        <div className="flex items-center gap-1">
          <label className="whitespace-nowrap text-xs font-medium text-gray-700">
            تخفیف:
          </label>
          <input
            type="number"
            value={formData.discount_percent}
            onChange={(e) => handleChange("discount_percent", e.target.value)}
            placeholder="0"
            className={inputClassName}
          />
        </div>

        <div className="flex items-center gap-1">
          <label className="whitespace-nowrap text-xs font-medium text-gray-700">
            حداقل:
          </label>
          <input
            type="number"
            value={formData.min_order}
            onChange={(e) => handleChange("min_order", e.target.value)}
            placeholder="0"
            className={inputClassName}
          />
        </div>

        <div className="flex items-center gap-1">
          <label className="whitespace-nowrap text-xs font-medium text-gray-700">
            حداکثر:
          </label>
          <input
            type="number"
            value={formData.max_order}
            onChange={(e) => handleChange("max_order", e.target.value)}
            placeholder="0"
            className={inputClassName}
          />
        </div>

        <div className="flex items-center gap-1">
          <label className="whitespace-nowrap text-xs font-medium text-gray-700">
            فعال:
          </label>
          <label className="relative inline-flex cursor-pointer items-center">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => handleChange("is_active", e.target.checked)}
              className="peer sr-only"
            />
            <div className="h-5 w-9 rounded-full bg-gray-200 transition-colors after:absolute after:start-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-green-600 peer-checked:after:translate-x-full peer-checked:after:border-white rtl:peer-checked:after:-translate-x-full" />
          </label>
        </div>

        <div className="mr-auto">
          <button
            onClick={handleSave}
            disabled={!isDirty || isSaving}
            className={`rounded-md px-3 py-1 text-xs font-medium transition-all duration-200 ${
              isDirty
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "cursor-not-allowed bg-gray-200 text-gray-500"
            }`}
          >
            {isSaving ? "..." : "ذخیره"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WarehouseCard;
